// 3rd-party modules
import { useEffect, useState } from "react";

// project modules
import { apiCall } from "../../helpers/apiHelper";
import { ReceiveSummeryReportModel } from "../../models/receiveSummeryReport";
import CustomChart from "../shared/chart/chart";

//apis
import * as ReportApi from '../../apis/reportApi';
import Loader from "../shared/loader";
import { formatMoney } from "../../helpers/objectHelper";
import useTranslation from "../../hooks/TranslationContext";
import { Radio, RadioChangeEvent, Typography } from "antd";

export default function ReceiveSummeryReport() {
  const { t } = useTranslation();

  const [period, setPeriod] = useState<number>(7);
  const [loading, setLoading] = useState(false);
  const [datasource, setDatasource] = useState<any[]>([]);
  const abortController = new AbortController();

  const chartOptions = {
    chart: {
      type: 'bar',
      height: 400,
      stacked: true,
      toolbar: {
        show: true
      },
      zoom: {
        enabled: false
      }
    },
    // title: {
    //   text: t('receive-summary-report'),
    //   align: 'left'
    // },
    responsive: [{
      breakpoint: 480,
      options: {
        legend: {
          position: 'bottom',
          offsetX: -10,
          offsetY: 0
        }
      }
    }],
    plotOptions: {
      bar: {
        horizontal: false,
        // borderRadius: 10,
        borderRadiusApplication: 'end', // 'around', 'end'
        borderRadiusWhenStacked: 'last', // 'all', 'last'
        dataLabels: {
          total: {
            enabled: true,
            style: {
              fontSize: '13px',
              fontWeight: 900
            }
          }
        }
      },
    },
    xaxis: {
      categories: Array.from(new Set(datasource.map((item) => item.date))),
      labels: {
        rotate: -45, // Rotates labels counter-clockwise
        rotateAlways: true, // Ensures the rotation is always applied
        style: {
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      title: { text: t("total-amount") },
      labels: {
        formatter: (value: any) => {
          return `${formatMoney(value / 10000000)}`;
        },
      },
    },
    dataLabels: {
      formatter: (value: any) => {
        return `${formatMoney(value / 10000000, "", "", 1)}`;
      },
    },
    tooltip: {
      y: {
        // Format tooltip values to 3 decimals
        formatter: (value: any) => {
          return formatMoney(value);
        },
      },
    },
    legend: {
      position: 'right',
      offsetY: 40
    },
    fill: {
      opacity: 1
    }
  };

  const [series, setSeries] = useState<any[]>([]);

  useEffect(() => {
    getDataAsync();
  }, [period]);

  useEffect(() => {
    // Group payments by date and bank
    const groupedData = datasource.reduce((acc, item) => {
      if (!acc[item.bankName]) acc[item.bankName] = {};
      acc[item.bankName][item.date] = (acc[item.bankName][item.date] || 0) + item.amount;
      return acc;
    }, {});

    // Extract banks and series data
    const banks = Object.keys(groupedData);

    const categories = Array.from(new Set(datasource.map((item) => item.date)));

    const series = banks.map((bank) => ({
      name: bank,
      data: categories.map((date) => groupedData[bank][date] || 0),
    }));

    // Update chart state
    setSeries(series);

  }, [datasource]);

  const getData = async (abortSignal?: AbortSignal) => {
    const response = await apiCall(ReportApi.getReceiveSummeryReport(period, abortSignal));

    const data = response.success ? ReceiveSummeryReportModel.toArrayOfClass((response.data?.value || [])) : [];

    return data.filter(x => !!x.amount)
  };

  const getDataAsync = async () => {
    setLoading(true);
    setDatasource(await getData(abortController.signal));
    setLoading(false);
  }

  const onPeriodChange = (e: RadioChangeEvent) => {
    setPeriod(e.target.value)
  };

  return (
    <>
      <Typography.Text strong>{t("receive-summary-report")}</Typography.Text>
      <div className="">
        <Radio.Group onChange={onPeriodChange} defaultValue="7">
          <Radio.Button value="1">1</Radio.Button>
          <Radio.Button value="3">3</Radio.Button>
          <Radio.Button value="7">7</Radio.Button>
          <Radio.Button value="30">30</Radio.Button>
          <Radio.Button value="90">90</Radio.Button>
        </Radio.Group>
      </div>
      {loading && <Loader />}
      {!loading &&
        <CustomChart
          options={chartOptions}
          series={series}
          type="bar"
          height={400}
          width="100%"
        />}
    </>
  )
}
